import {
    Component,
    ChangeDetectionStrategy,
    input,
    inject,
    signal,
    computed,
    effect,
    untracked,
    OnInit,
    DestroyRef,
    ChangeDetectorRef
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Tooltip } from '@openng/optimus-ui/tooltip';
import { SidebarService } from '../../../core/services/sidebar.service';

/**
 * Internal representation of a menu item with collapse state.
 * Built from NavigationService's AppMenuItem.
 */
export interface SidenavMenuItem {
    id: number;
    label: string;
    icon: string;
    route: string | null;
    href: string | null;
    visible: boolean;
    children: SidenavMenuItem[];
    collapsed: boolean;
}

@Component({
    selector: 'app-sidenav-menu',
    standalone: true,
    imports: [Tooltip],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './sidenav-menu.component.html',
    styleUrl: './sidenav-menu.component.css'
})
export class SidenavMenuComponent implements OnInit {
    /** Menu items from parent (NavigationComponent) */
    items = input.required<SidenavMenuItem[]>();

    private readonly router = inject(Router);
    private readonly destroyRef = inject(DestroyRef);
    protected readonly sidebarService = inject(SidebarService);
    private readonly cdr = inject(ChangeDetectorRef);

    /** Current router URL for active route highlighting */
    readonly currentUrl = signal(this.router.url);

    /**
     * URL used for highlighting. A page with no menu entry of its own counts as
     * its listing: `/persons/edit/406?person_type=authority` highlights the entry
     * for `/persons/list?person_type=authority`, and entity-first pages such as
     * `/events/42/edit` or `/events/42/guests` highlight `/events/list`.
     */
    readonly highlightUrl = computed(() => {
        const url = this.currentUrl();
        const items = this.items();
        if (this.anyItemMatches(items, url)) {
            return url;
        }
        return SidenavMenuComponent.listUrlCandidates(url).find(candidate => this.anyItemMatches(items, candidate)) ?? url;
    });

    constructor() {
        // Expand the branch of the active entry whenever the URL changes or the menu
        // items arrive (they load from the API after the component is created, so a
        // one-off expand at init found an empty menu on direct loads and reloads).
        effect(() => {
            const items = this.items();
            this.highlightUrl();
            untracked(() => {
                this.expandActivePaths(items);
                this.cdr.markForCheck();
            });
        });
    }

    ngOnInit(): void {
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(e => this.currentUrl.set(e.urlAfterRedirects));
    }

    /** Toggle collapse/expand for a nested parent item */
    toggleItem(item: SidenavMenuItem): void {
        if (this.isCondensed()) return;
        item.collapsed = !item.collapsed;
    }

    /** Toggle top-level menu item con comportamiento acordeón:
     *  abrir uno cierra el resto de los top-level. */
    toggleTopLevelItem(item: SidenavMenuItem): void {
        if (this.isCondensed()) return;
        const willOpen = item.collapsed;
        if (willOpen) {
            for (const sibling of this.items()) {
                if (sibling !== item && !sibling.collapsed) {
                    sibling.collapsed = true;
                }
            }
        }
        item.collapsed = !item.collapsed;
    }

    /** True when the sidebar is in condensed (icon-only) mode. */
    isCondensed(): boolean {
        return document.documentElement.getAttribute('data-sidenav-size') === 'condensed';
    }

    /** Navigate to a route and close mobile drawer */
    navigateTo(route: string): void {
        this.router.navigateByUrl(route);
        this.sidebarService.closeMobile();
    }

    /**
     * Check if an item's route matches the current page. A parent whose own route
     * is also a child's (Personas and Todos both point at /persons/list) yields
     * to that child, so only the leaf is highlighted.
     */
    isActive(item: SidenavMenuItem): boolean {
        if (!this.matchesUrl(item, this.highlightUrl())) return false;
        return !(item.children ?? []).some(child => this.isChildActive(child));
    }

    private anyItemMatches(items: SidenavMenuItem[], url: string): boolean {
        return items.some(item => this.matchesUrl(item, url) || this.anyItemMatches(item.children ?? [], url));
    }

    /**
     * Listing URLs a page can fall back to, most specific first, keeping the query:
     * `/x/edit[/42]` → `/x/list`, then any other `/x/...` → `/x/list`.
     */
    private static listUrlCandidates(url: string): string[] {
        const [path, query] = url.split('?');
        const suffix = query ? '?' + query : '';
        const candidates: string[] = [];
        const edit = /^(\/.+?)\/edit(\/[^/]*)?\/?$/.exec(path);
        if (edit) {
            candidates.push(edit[1] + '/list' + suffix);
        }
        const firstSegment = /^\/([^/]+)\/./.exec(path);
        if (firstSegment) {
            candidates.push('/' + firstSegment[1] + '/list' + suffix, '/' + firstSegment[1] + '/list');
        }
        return candidates.filter(candidate => candidate.split('?')[0] !== path);
    }

    private matchesUrl(item: SidenavMenuItem, url: string): boolean {
        if (!item.route) return false;
        const current = this.parseRoute(url);
        const target = this.parseRoute(item.route);

        if (!this.isPathMatch(current.path, target.path)) {
            return false;
        }

        // If menu item specifies query params, they must match the current URL.
        if (target.query.size > 0) {
            return this.hasMatchingQueryParams(current.query, target.query);
        }

        // Avoid double-highlight: "/persons/list" should not be active when a specific person_type is selected.
        if (target.path === '/persons/list' && current.query.has('person_type')) {
            return false;
        }

        return true;
    }

    /** True si este item es un ancestro de la ruta activa (no es el leaf). */
    isParentActive(item: SidenavMenuItem): boolean {
        if (this.isActive(item)) return false;
        return this.hasChildren(item) && this.isChildActive(item);
    }

    /** Check if any descendant is active (for parent highlighting + auto-expand) */
    isChildActive(item: SidenavMenuItem): boolean {
        if (this.isActive(item)) return true;
        if (!item.children || item.children.length === 0) return false;
        return item.children.some(child => this.isChildActive(child));
    }

    /** Has visible children */
    hasChildren(item: SidenavMenuItem): boolean {
        return item.children && item.children.filter(c => c.visible).length > 0;
    }

    /** Auto-expand parents that contain active routes */
    private expandActivePaths(items: SidenavMenuItem[]): void {
        for (const item of items) {
            if (this.hasChildren(item)) {
                if (this.isChildActive(item)) {
                    item.collapsed = false;
                }
                this.expandActivePaths(item.children);
            }
        }
    }

    private parseRoute(route: string): { path: string; query: URLSearchParams } {
        const [pathPart, queryPart = ''] = route.split('?');
        return {
            path: this.normalizePath(pathPart || '/'),
            query: new URLSearchParams(queryPart)
        };
    }

    private normalizePath(path: string): string {
        if (!path) return '/';
        if (path === '/') return path;
        return path.endsWith('/') ? path.slice(0, -1) : path;
    }

    private isPathMatch(currentPath: string, targetPath: string): boolean {
        return currentPath === targetPath || currentPath.startsWith(targetPath + '/');
    }

    private hasMatchingQueryParams(current: URLSearchParams, target: URLSearchParams): boolean {
        for (const [key, value] of target.entries()) {
            // Auxiliary navigation flags carried by menu links (e.g. `reset=True` to
            // wipe stale filters when navigating from the sidebar) are NOT part of
            // the route identity — ignore them when comparing against the live URL.
            if (SidenavMenuComponent.AUX_QUERY_PARAMS.has(key.toLowerCase())) continue;
            if (current.get(key) !== value) {
                return false;
            }
        }
        return true;
    }

    private static readonly AUX_QUERY_PARAMS = new Set<string>(['reset']);
}
